import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button, Flex, Typography, Row, Col, Card, Skeleton, message, theme, Alert } from 'antd';
import { LeftOutlined } from '@ant-design/icons';

import AdminTemplate from '../../layout/AdminTemplate';
import {
  useGetDomainDetailsQuery,
  useGetExtraStatsQuery,
  useUpdateDomainDetailsMutation,
} from './api/domainManagerApiSlice';
import CacheAgeChart from './CacheAgeChart';
import DomainCustomCacheExpiration from './DomainCustomCacheExpiration';
import DomainStats from './DomainStats';
import CacheAgeTable from './CacheAgeTable';
import PatternBasedRules from './components/PatternBasedRules';
import {
  useGetSchedulerSettingsQuery,
  useAddSchedulerSettingsMutation,
  useDeleteSchedulerSettingsMutation,
} from './api/schedulerSettingsApiSlice';
import useFeatureFlag, { FeatureFlag } from '../../hooks/useFeatureFlag';
import { useCurrentPlan } from '../../hooks/usePrerenderUser';
import isCacheSettingsAllowedByPlan from './utils/isCacheSettingsAllowedByPlan';
import { UpgradePlanProvider } from '~features/UpgradePlan';

const META_TAGS = {
  title: 'Domain Manager Details - Prerender.io',
};

const oneMonthAgo = new Date();
oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
const today = new Date();

function DomainDetailsPage() {
  const { domain } = useParams();
  const { token } = theme.useToken();
  const currentPlan = useCurrentPlan();

  const isDomainSchedulerSettingsEnabled = useFeatureFlag([FeatureFlag.DomainSchedulerSettings]);
  const isDomainDetailsEnabled = useFeatureFlag([FeatureFlag.DomainsDetails]);
  const cacheSettingsAllowedByPlan = isCacheSettingsAllowedByPlan(currentPlan.id);

  const { data: domainDetails, isLoading: isLoadingDetails, isError: fetchingError } = useGetDomainDetailsQuery(domain, { refetchOnMountOrArgChange: true });
  const [updateDomainDetails, { isLoading: isUpdating, isError: updatingError, isSuccess: updatingSuccess }] =
    useUpdateDomainDetailsMutation();
  const extraStatsResult = useGetExtraStatsQuery({ domain, from: oneMonthAgo.toISOString(), to: today.toISOString() }, { refetchOnMountOrArgChange: true });

  const [messageApi, contextHolder] = message.useMessage();
  const {
    data: schedulerSettings,
    isLoading: isLoadingSettings,
    isError: fetchingSettingsError,
  } = useGetSchedulerSettingsQuery(domain, { refetchOnMountOrArgChange: true });
  const [
    addSchedulerSettings,
    { isLoading: isAddSettingLoading, isSuccess: addingSettingSuccess, error: addSettingError },
  ] = useAddSchedulerSettingsMutation();
  const [
    deleteSchedulerSettings,
    { isLoading: isDeleteSettingLoading, isError: deletingSettingError, isSucess: deletingSettingSuccess },
  ] = useDeleteSchedulerSettingsMutation();

  const updateSettings = (settings) => {
    updateDomainDetails({
      domain,
      slowRenderTriggerSeconds: settings.slowRenderTrigger,
      cacheLifetimeHours: settings.cacheExpirationHours,
      dataRetentionDays: settings.dataRetentionDays,
    });
  };

  const onAddSetting = (settings) => {
    const cacheExpirationValue =
      settings.cacheExpirationHours > 24 ? (settings.cacheExpirationHours - 23) * 24 : settings.cacheExpirationHours;
    addSchedulerSettings({
      domain,
      rule: settings.rule,
      cache_freshness_hours: cacheExpirationValue,
    });
  };

  useEffect(() => {
    if (fetchingError)
      messageApi.error({
        content: 'Failed to fetch domain details',
      });

    if (updatingError)
      messageApi.error({
        content: 'Failed to update scheduler settings',
      });

    if (updatingSuccess)
      messageApi.success({
        content: 'Scheduler settings updated successfully',
      });
  }, [fetchingError, updatingError, updatingSuccess]);

  useEffect(() => {
    if (fetchingSettingsError)
      messageApi.error({
        content: 'Failed to fetch scheduler settings',
      });
    if (addSettingError) {
      messageApi.error({
        content: addSettingError?.data?.message,
      });
    }
    if (deletingSettingError)
      messageApi.error({
        content: 'Failed to delete rule',
      });
    if (addingSettingSuccess)
      messageApi.success({
        content: 'Scheduler settings rule added successfully',
      });
    if (deletingSettingSuccess)
      messageApi.success({
        content: 'Scheduler settings rule delete successfully',
      });
  }, [fetchingSettingsError, addSettingError, deletingSettingError, addingSettingSuccess, deletingSettingSuccess]);


  const hasAccessToDomainCustomCacheExpiration = isDomainDetailsEnabled || cacheSettingsAllowedByPlan
  const hasAccessToPatternBasedRules = isDomainSchedulerSettingsEnabled || cacheSettingsAllowedByPlan;
  const hasAccessToSchedulerSettings = hasAccessToDomainCustomCacheExpiration || hasAccessToPatternBasedRules;
  
  return (
    <AdminTemplate metaTags={META_TAGS}>
      {contextHolder}
      <Flex vertical gap="large">
        <Flex gap="large">
          <Link to="/domain-manager">
            <Button icon={<LeftOutlined />} type="link">
              Back
            </Button>
          </Link>
          <Typography.Title level={3}>Custom Cache Expiration Rules - {domain}</Typography.Title>
        </Flex>

        <DomainStats
          isLoading={isLoadingDetails}
          uniquePages={domainDetails?.pagesCount}
          firstSeenAt={domainDetails?.createdAt}
          averageCacheMinutes={domainDetails?.avgCacheMinutes}
          pagesNotCrawled={extraStatsResult.data?.notCrawledPages}
        />

        <Row gutter={[token.marginMD, token.marginMD]}>
          <Col xs={24} xl={12}>
            <Card title="Cache Age">
              <Skeleton loading={isLoadingDetails} active>
                <CacheAgeChart
                  lessOneDay={domainDetails?.numCacheToday}
                  gOneLSevenDays={domainDetails?.numCache_g1l7}
                  gSevenLThirtyDays={domainDetails?.numCache_g7l30}
                  gThirtyDays={domainDetails?.numCache_g30}
                />
                <CacheAgeTable
                  lessOneDay={domainDetails?.numCacheToday}
                  gOneLSevenDays={domainDetails?.numCache_g1l7}
                  gSevenLThirtyDays={domainDetails?.numCache_g7l30}
                  gThirtyDays={domainDetails?.numCache_g30}
                />
              </Skeleton>
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <UpgradePlanProvider>
              <Card
                title="Scheduler Settings"
                styles={{ body: { padding: `${token.paddingSM}px ${token.paddingXS}px` } }}
              >
                <Skeleton loading={isLoadingDetails || isLoadingSettings} active>
                  {hasAccessToSchedulerSettings && <Alert style={{ marginBottom: token.margin, fontSize: token.fontSizeSM }} message="When you update cache expiration settings, the new rule does not immediately trigger a recache. You may still see URLs recaching in the Cache Manager according to their original cache expiration interval. Pages will complete their current cycle using the original cache expiration setting. After that they will follow the new cache expiration rules." type="warning" />}

                  {hasAccessToDomainCustomCacheExpiration && (
                    <Card type="inner" title="Domain Custom Cache Expiration" size="small">
                      <DomainCustomCacheExpiration
                        isLoading={isUpdating}
                        disabled={fetchingError}
                        onSave={updateSettings}
                        slowRenderTrigger={domainDetails?.slowRenderTriggerSeconds}
                        cacheExpirationHours={domainDetails?.cacheLifetimeHours}
                        dataRetentionDays={domainDetails?.dataRetentionDays ?? null}
                        planRetentionDays={currentPlan.dataRetentionDays}
                        cacheExpirationDaysInterval={currentPlan.cacheRefreshInterval}
                      />
                    </Card>
                  )}
                  {hasAccessToPatternBasedRules && (
                    <Card type="inner" title="Pattern-based Rules" size="small" style={{ marginTop: token.margin }}>
                      <PatternBasedRules
                        schedulerSettings={schedulerSettings?.data}
                        isLoading={isDeleteSettingLoading}
                        onDeleteSetting={deleteSchedulerSettings}
                        onAddSetting={onAddSetting}
                        isAddSettingLoading={isAddSettingLoading}
                        cacheExpirationDaysInterval={currentPlan.cacheRefreshInterval}
                      />
                    </Card>
                  )}
                </Skeleton>
              </Card>
            </UpgradePlanProvider>
          </Col>
        </Row>
      </Flex>
    </AdminTemplate>
  );
}

export default DomainDetailsPage;
